import { z } from "zod";
import i18next from "i18next";
import { MemberRole } from "@/server/types/member";

// TO DO => extract to a global utility function
// Create a safe translation function that returns fallback if i18next isn't ready
const safeTranslate = (key: string, fallback: string): string => {
  try {
    // Check if i18next is initialized and has the namespace loaded
    if (
      i18next.isInitialized &&
      i18next.hasResourceBundle(i18next.language, "common")
    ) {
      return i18next.t(key, { defaultValue: fallback });
    }
    return fallback;
  } catch {
    return fallback;
  }
};

// GroupChatFormSchema -> Zod Schema -> Login Form
export const GroupChatFormSchema = z
  .object({
    avatar: z.string(),
    title: z
      .string()
      .min(3, {
        message: safeTranslate(
          "groupChat.validation.titleMinLength",
          "Group title must be at least 3 characters.",
        ),
      })
      .max(40, {
        message: safeTranslate(
          "groupChat.validation.titleMaxLength",
          "Group title must be at most 40 characters.",
        ),
      })
      .regex(/^[a-zA-Z0-9._()\- ]+$/, {
        message: safeTranslate(
          "groupChat.validation.titleCharacters",
          "Title may only contain letters (any case), numbers, periods, underscores, parentheses, hyphens, or spaces.",
        ),
      }),

    isSearchable: z.boolean(),
    description: z.string().max(200, {
      message: safeTranslate(
        "groupChat.validation.descriptionMaxLength",
        "Group description must be at most 200 characters.",
      ),
    }),

    allowThreads: z.boolean(),
    canPost: z.nativeEnum(MemberRole),
  })
  .refine(
    (data) => {
      // If chat is searchable, description must be at least 3 characters
      // If chat is not searchable, description can be empty
      return !data.isSearchable || data.description.length >= 3;
    },
    {
      message: safeTranslate(
        "groupChat.validation.descriptionMinLengthWhenSearchable",
        "Group description must be at least 3 characters when chat is searchable.",
      ),
      path: ["description"], // This targets the error at the description field
    },
  );

// NewGroupChatForm -> Infer the type from the Zod Schema -> CReate nre group chat Form
export type GroupChatFormType = z.infer<typeof GroupChatFormSchema>;

// ArchitectEditFormSchema -> Edit dialog for architect hub chats.
// Only title/avatar are user-editable; the other fields are carried through
// from server state without UI, so the `isSearchable => description>=3` refine
// from GroupChatFormSchema must not apply here or it could silently block
// submitting a plain title change.
export const ArchitectEditFormSchema = z.object({
  avatar: z.string(),
  title: z
    .string()
    .min(3, {
      message: safeTranslate(
        "groupChat.validation.titleMinLength",
        "Group title must be at least 3 characters.",
      ),
    })
    .max(40, {
      message: safeTranslate(
        "groupChat.validation.titleMaxLength",
        "Group title must be at most 40 characters.",
      ),
    })
    .regex(/^[a-zA-Z0-9._()\- ]+$/, {
      message: safeTranslate(
        "groupChat.validation.titleCharacters",
        "Title may only contain letters (any case), numbers, periods, underscores, parentheses, hyphens, or spaces.",
      ),
    }),
  isSearchable: z.boolean(),
  description: z.string(),
  allowThreads: z.boolean(),
  canPost: z.nativeEnum(MemberRole),
});

// AIChatFormSchema -> Zod Schema -> New Ai chat Form
export const AIChatFormSchema = z.object({
  // min(0) allows attachment-only messages (no text required when a file is attached)
  prompt: z.string().max(10000, {
    message: safeTranslate(
      "aiChat.validation.promptMaxLength",
      "Prompt message must be at most 10k characters.",
    ),
  }),
});

// NewGroupChatForm -> Infer the type from the Zod Schema -> CReate nre group chat Form
export type AIChatFormType = z.infer<typeof AIChatFormSchema>;
